import * as React from 'react';
import propz from 'propz';
import { Loader } from '../../../../components/Loader/Loader';
import { AGE_GROUPS_SORTED } from '../../../../consts/school';
import { LIMIT } from '../../../../consts/table';
import { formatAgesCompact, resolveAgeNamingFromContext, formatSingleAgeLabel } from '../../../../helpers/ageGroups';
import { getClubs, getClubsCount, getClubsSports, ClubsSportOption } from '../../../../helpers/service/admin/clubs';
import { getClubStudentsCount } from '../../../../helpers/service/admin/clubStudents';
import { addProgrammeClubsBatch, getAllClubProgrammes } from '../../../../helpers/service/admin/clubProgrammes';
import {
  AddMode,
  getErrMsg,
  getClubId,
  getClubDay,
  getClubSportName,
  getClubStartIso,
  fmtDMY,
  isOneDayClub,
  isStartedOrPast,
  isFinishedOrPast,
  isClubRemoved,
  buildAddWhere
} from 'Src/helpers/clubProgramme/clubProgramme';
import { useCloseOnOutside } from '../../../../hooks/useCloseOnOutside';
import { getAllForms } from '../../../../helpers/service/admin/forms';
import { SchoolForm } from '../../../../models/form';
import { AfterSchoolClubProgramme, ClubProgrammeClubItem } from '../../../../models/clubProgramme';
import { ClubProgrammeBadge } from '../../../../types/clubProgramme';
import { AppUser } from '../../../../views/App/App';
import {
  ADD_CLUBS_COLUMNS,
  AddClubsColumnKey,
  ClubProgrammeAddClubsTable
} from './components/ClubProgrammeAddClubsTable';
import { ClubProgrammeAddClubsFilters } from './components/ClubProgrammeAddClubsFilters';
import { ClubProgrammeAddClubsActionBar } from './components/ClubProgrammeAddClubsActionBar';
import { WEEK_DAYS_ORDER, getProgrammeClubDayShortLabel, getProgrammeClubGenderLabel } from './helpers/clubProgrammeClubsContent';

const AddModalIcon: React.FC<{ icon: 'search' | 'whistle'; size?: number }> = ({ icon, size = 16 }) => {
  const paths = {
    search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.35-4.35',
    whistle: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z'
  };

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.85"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[icon]} />
    </svg>
  );
};
type Props = {
  user: AppUser;
  programme: AfterSchoolClubProgramme;
  onProgrammeUpdated: (p: AfterSchoolClubProgramme) => void;
  variant?: 'page' | 'modal';
  onClose?: () => void;
};

export const ClubProgrammeAddClubsContent: React.FC<Props> = ({ user, programme, onProgrammeUpdated, variant = 'page', onClose }) => {
  const [addErr, setAddErr] = React.useState<string>('');

  const canEdit = String((programme as any).status || 'DRAFT') === 'DRAFT';
  const programmeId = String((programme as any).id || (programme as any)._id || '');

  const naming = React.useMemo(() => resolveAgeNamingFromContext(user, programme), [user, programme]);
  const getClubAgesLabel = (c: any) => formatAgesCompact(propz.get(c, ['ages'], []), naming);

  const programmeClubs: any[] = programme && (programme as any).clubs ? ((programme as any).clubs as any[]) || [] : [];

  const [excludeClubIds, setExcludeClubIds] = React.useState<string[]>([]);
  const [otherProgrammeClubIds, setOtherProgrammeClubIds] = React.useState<string[]>([]);

  React.useEffect(() => {
    const currentProgrammeClubIds = (programmeClubs || []).map((pc: any) => String(pc && pc.clubId)).filter(Boolean);

    const merged = Array.from(new Set([...currentProgrammeClubIds, ...otherProgrammeClubIds]));
    const mergedKey = merged.join('|');

    setExcludeClubIds(prev => {
      const prevKey = (prev || []).join('|');
      return prevKey === mergedKey ? prev : merged;
    });
  }, [
    otherProgrammeClubIds.join('|'),
    (programmeClubs || []).map((pc: any) => String((pc && pc.clubId) || '')).join('|')
  ]);

  React.useEffect(() => {
    if (!programmeId) {
      setOtherProgrammeClubIds([]);
      return;
    }

    getAllClubProgrammes(user, { _isRemoved: { $ne: true } })
      .then((items: any[]) => {
        const otherIdsMap: { [id: string]: boolean } = {};

        (Array.isArray(items) ? items : []).forEach((p: any) => {
          const pId = String((p && (p._id || p.id)) || '');
          if (!pId || pId === String(programmeId)) return;

          const clubs = Array.isArray(p && p.clubs) ? p.clubs : [];
          clubs.forEach((pc: any) => {
            const clubId = String((pc && pc.clubId) || '');
            if (clubId) otherIdsMap[clubId] = true;
          });
        });

        setOtherProgrammeClubIds(Object.keys(otherIdsMap));
      })
      .catch(() => {
        setOtherProgrammeClubIds([]);
      });
  }, [user, programmeId]);

  const [isProgrammeClubsLoading, setIsProgrammeClubsLoading] = React.useState(false);

  const refreshProgrammeClubDetails = React.useCallback(() => {
    if (refreshProgrammeTimerRef.current) {
      window.clearTimeout(refreshProgrammeTimerRef.current);
    }

    setIsProgrammeClubsLoading(true);

    refreshProgrammeTimerRef.current = window.setTimeout(() => {
      setIsProgrammeClubsLoading(false);
      refreshProgrammeTimerRef.current = null;
    }, 250);
  }, []);

  const getClubBadges = React.useCallback((club: any): ClubProgrammeBadge[] => {
    if (!club) return [{ kind: 'warn', text: 'Not found' }];

    const badges: ClubProgrammeBadge[] = [];
    const removed = isClubRemoved(club);
    const status = String(club.status || '');

    if (removed) badges.push({ kind: 'danger', text: 'Deleted' });
    if (isStartedOrPast(club)) badges.push({ kind: 'warn', text: 'Started' });
    if (!isOneDayClub(club)) badges.push({ kind: 'info', text: 'Multiple days' });
    if (status && status !== 'DRAFT') badges.push({ kind: 'info', text: `Status: ${status}` });
    if (isFinishedOrPast(club)) badges.push({ kind: 'warn', text: 'No longer eligible' });

    if (badges.length === 0) badges.push({ kind: 'ok', text: 'Eligible' });
    return badges;
  }, []);

  const getInitialAddGenderFiltersFromProgramme = React.useCallback(() => {
    const programmeGendersRaw = propz.get(programme, ['audience', 'genders'], []);
    const programmeGenders = Array.isArray(programmeGendersRaw)
      ? programmeGendersRaw
          .map((x: any) =>
            String(x || '')
              .trim()
              .toUpperCase()
          )
          .filter((x: string) => x === 'MALE' || x === 'FEMALE')
      : [];

    const hasMale = programmeGenders.includes('MALE');
    const hasFemale = programmeGenders.includes('FEMALE');

    if (hasMale && hasFemale) return [];
    if (hasFemale) return ['FEMALE_ONLY', 'MIXED'];
    if (hasMale) return ['MALE_ONLY', 'MIXED'];

    return [];
  }, [programme]);

  const getInitialAddAgeValuesFromProgramme = React.useCallback(() => {
    const programmeAgesRaw = propz.get(programme, ['audience', 'ages'], []);
    return Array.isArray(programmeAgesRaw)
      ? programmeAgesRaw
          .map((x: any) => Number(x))
          .filter((x: number) => Number.isInteger(x) && x >= 0)
          .map((x: number) => Math.trunc(x))
      : [];
  }, [programme]);

  const [isAddWorking, setIsAddWorking] = React.useState(false);
  const [isAddFiltersOpen, setIsAddFiltersOpen] = React.useState(true);
  const [addHiddenColumns, setAddHiddenColumns] = React.useState<AddClubsColumnKey[]>([]);

  const addToggleColumn = React.useCallback((key: AddClubsColumnKey) => {
    const column = ADD_CLUBS_COLUMNS.find(item => item.key === key);
    if (!column || column.locked) return;

    setAddHiddenColumns(prev => (prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]));
  }, []);
  const [addMode, setAddMode] = React.useState<AddMode>('ELIGIBLE_ONLY');
  const [addSearch, setAddSearch] = React.useState('');
  const [appliedAddSearch, setAppliedAddSearch] = React.useState('');
  const [addSportIds, setAddSportIds] = React.useState<string[]>([]);
  const [addSportDropdownOpen, setAddSportDropdownOpen] = React.useState(false);
  const [addSportSearch, setAddSportSearch] = React.useState('');
  const addActivitiesWrapRef = React.useRef<HTMLDivElement | null>(null);

  const [addGenders, setAddGenders] = React.useState<string[]>(() => getInitialAddGenderFiltersFromProgramme());
  const [addGenderDropdownOpen, setAddGenderDropdownOpen] = React.useState(false);
  const addGenderWrapRef = React.useRef<HTMLDivElement | null>(null);

  const [addAgeValues, setAddAgeValues] = React.useState<number[]>(() => getInitialAddAgeValuesFromProgramme());

  const [addAgeDropdownOpen, setAddAgeDropdownOpen] = React.useState(false);
  const [addAgeSearch, setAddAgeSearch] = React.useState('');
  const addAgeWrapRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const nextGenders = getInitialAddGenderFiltersFromProgramme();
    const nextAges = getInitialAddAgeValuesFromProgramme();

    setAddGenders(nextGenders);
    setAddAgeValues(nextAges);
  }, [getInitialAddGenderFiltersFromProgramme, getInitialAddAgeValuesFromProgramme]);

  const [addCountsByClubId, setAddCountsByClubId] = React.useState<{
    [clubId: string]: { eligible?: number; allow?: number };
  }>({});
  const [addCountsLoadingByClubId, setAddCountsLoadingByClubId] = React.useState<{ [clubId: string]: boolean }>({});

  const [addDays, setAddDays] = React.useState<string[]>([]);
  const [addDayDropdownOpen, setAddDayDropdownOpen] = React.useState(false);
  const addDayWrapRef = React.useRef<HTMLDivElement | null>(null);

  const [addStartFrom, setAddStartFrom] = React.useState('');
  const [addStartTo, setAddStartTo] = React.useState('');

  const [addItems, setAddItems] = React.useState<ClubProgrammeClubItem[]>([]);
  const [addTotal, setAddTotal] = React.useState(0);
  const [addPage, setAddPage] = React.useState(1);

  const [addSelectMenuOpen, setAddSelectMenuOpen] = React.useState(false);
  const addSelectMenuWrapRef = React.useRef<HTMLDivElement | null>(null);

  const refreshProgrammeTimerRef = React.useRef<number | null>(null);

  const addLoadRequestRef = React.useRef(0);

  const [addAllSelected, setAddAllSelected] = React.useState(false);
  const [addSelectedIds, setAddSelectedIds] = React.useState<{ [id: string]: boolean }>({});
  const [addDeselectedIds, setAddDeselectedIds] = React.useState<{ [id: string]: boolean }>({});
  const [addAllSelectedWhere, setAddAllSelectedWhere] = React.useState<any>(null);

  const addIsSelected = (id: string) => {
    const k = String(id || '');
    if (!k) return false;
    if (addAllSelected) return !addDeselectedIds[k];
    return !!addSelectedIds[k];
  };

  const addGetSelectedExplicitIds = () => Object.keys(addSelectedIds).filter(k => !!addSelectedIds[k]);
  const addGetDeselectedExplicitIds = () => Object.keys(addDeselectedIds).filter(k => !!addDeselectedIds[k]);

  const addSelectedCount = React.useMemo(() => {
    if (addAllSelected) {
      const excluded = Object.keys(addDeselectedIds).filter(k => addDeselectedIds[k]).length;
      return Math.max(0, (addTotal || 0) - excluded);
    }
    return addGetSelectedExplicitIds().length;
  }, [addAllSelected, addDeselectedIds, addSelectedIds, addTotal]);

  const addHasAnySelection = React.useMemo(() => {
    if (addAllSelected) return (addTotal || 0) > 0;
    return addGetSelectedExplicitIds().length > 0;
  }, [addAllSelected, addSelectedIds, addTotal]);

  const addClearSelection = React.useCallback(() => {
    setAddAllSelected(false);
    setAddSelectedIds({});
    setAddDeselectedIds({});
    setAddAllSelectedWhere(null);
  }, []);

  const addNormalizeWhereForSelectAll = (whereRaw: any) => {
    if (whereRaw && Object.keys(whereRaw).length > 0) return whereRaw;
    return { _id: { $exists: true } };
  };

  const addDaysLabel = React.useMemo(() => {
    if (!addDays || addDays.length === 0) return 'All days';
    if (addDays.length === WEEK_DAYS_ORDER.length) return 'All days';
    const sorted = [...addDays].sort((a, b) => WEEK_DAYS_ORDER.indexOf(a) - WEEK_DAYS_ORDER.indexOf(b));
    return sorted.map(d => getProgrammeClubDayShortLabel(d)).join(', ');
  }, [addDays.join('|')]);

  const addAgesLabel = React.useMemo(() => {
    if (!addAgeValues || addAgeValues.length === 0) return 'All ages';
    return formatAgesCompact(addAgeValues, naming);
  }, [addAgeValues.join('|'), naming]);

  const addGenderOptions = React.useMemo(() => {
    const programmeGendersRaw = propz.get(programme, ['audience', 'genders'], []);
    const programmeGenders = Array.isArray(programmeGendersRaw)
      ? programmeGendersRaw
          .map((x: any) =>
            String(x || '')
              .trim()
              .toUpperCase()
          )
          .filter((x: string) => x === 'MALE' || x === 'FEMALE')
      : [];

    const hasMale = programmeGenders.includes('MALE');
    const hasFemale = programmeGenders.includes('FEMALE');

    if (hasMale && hasFemale) {
      return [
        { id: 'MALE_ONLY', label: 'Boys' },
        { id: 'FEMALE_ONLY', label: 'Girls' },
        { id: 'MIXED', label: 'Mixed' }
      ];
    }

    if (hasFemale) {
      return [
        { id: 'FEMALE_ONLY', label: 'Girls' },
        { id: 'MIXED', label: 'Mixed' }
      ];
    }

    if (hasMale) {
      return [
        { id: 'MALE_ONLY', label: 'Boys' },
        { id: 'MIXED', label: 'Mixed' }
      ];
    }

    return [
      { id: 'MALE_ONLY', label: 'Boys' },
      { id: 'FEMALE_ONLY', label: 'Girls' },
      { id: 'MIXED', label: 'Mixed' }
    ];
  }, [programme]);

  const [sportOptions, setSportOptions] = React.useState<ClubsSportOption[]>([]);
  const [isSportsLoading, setIsSportsLoading] = React.useState<boolean>(false);

  const [forms, setForms] = React.useState<SchoolForm[]>([]);

  const ageOptions = React.useMemo(() => {
    const namingKey = String(naming || 'ENGLISH');
    const sortOrder: string[] = (AGE_GROUPS_SORTED as any)[namingKey] || [];

    const schoolAges = Array.from(
      new Set(
        (Array.isArray(forms) ? forms : [])
          .map((form: any) => Number(propz.get(form, ['age'], NaN)))
          .filter((age: number) => Number.isFinite(age))
          .map((age: number) => Math.trunc(age))
      )
    );

    const programmeAudienceAgesRaw = propz.get(programme, ['audience', 'ages'], []);
    const programmeAudienceAges = Array.isArray(programmeAudienceAgesRaw)
      ? Array.from(
          new Set(
            programmeAudienceAgesRaw
              .map((age: any) => Number(age))
              .filter((age: number) => Number.isFinite(age))
              .map((age: number) => Math.trunc(age))
          )
        )
      : [];

    const allowedAges =
      programmeAudienceAges.length > 0 ? schoolAges.filter(age => programmeAudienceAges.includes(age)) : schoolAges;

    return allowedAges
      .map(age => ({
        age,
        label: formatSingleAgeLabel(age, namingKey)
      }))
      .sort((a, b) => {
        const aIndex = sortOrder.indexOf(a.label);
        const bIndex = sortOrder.indexOf(b.label);

        const safeA = aIndex === -1 ? 9999 : aIndex;
        const safeB = bIndex === -1 ? 9999 : bIndex;

        if (safeA !== safeB) return safeA - safeB;
        return a.age - b.age;
      });
  }, [forms, naming, programme]);

  const refreshSportsList = React.useCallback(() => {
    setIsSportsLoading(true);
    setAddErr('');

    const baseWhere: any = {
      status: 'DRAFT',
      'schedule.days.0': { $exists: true },
      'schedule.days.1': { $exists: false }
    };

    if (addMode === 'ELIGIBLE_ONLY') {
      baseWhere['schedule.startDate'] = { $gt: new Date().toISOString() };
    }

    return getClubsSports(user, baseWhere).then(
      (list: any) => {
        const raw = Array.isArray(list) ? list : [];

        const normalized: ClubsSportOption[] = raw
          .map((x: any) => ({
            id: String(x.id || x.sportId || x._id || ''),
            name: String(x.name || x.sportName || x.title || '')
          }))
          .filter((x: any) => !!x.id)
          .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)));

        setSportOptions(normalized);
        setIsSportsLoading(false);
        return true;
      },
      (e: any) => {
        setSportOptions([]);
        setIsSportsLoading(false);
        setAddErr(getErrMsg(e, 'Failed to load activities'));
        return false;
      }
    );
  }, [user, addMode]);

  const refreshFormsList = React.useCallback(() => {
    return getAllForms(user).then(
      (items: any) => {
        setForms(Array.isArray(items) ? items : []);
        return true;
      },
      (e: any) => {
        setForms([]);
        setAddErr(getErrMsg(e, 'Failed to load forms'));
        return false;
      }
    );
  }, [user]);

  useCloseOnOutside({
    isOpen: addSportDropdownOpen,
    onClose: () => setAddSportDropdownOpen(false),
    wrapRef: addActivitiesWrapRef as any,
    enabled: true,
    gate: true
  });
  useCloseOnOutside({
    isOpen: addGenderDropdownOpen,
    onClose: () => setAddGenderDropdownOpen(false),
    wrapRef: addGenderWrapRef as any,
    enabled: true,
    gate: true
  });
  useCloseOnOutside({
    isOpen: addAgeDropdownOpen,
    onClose: () => setAddAgeDropdownOpen(false),
    wrapRef: addAgeWrapRef as any,
    enabled: true,
    gate: true
  });
  useCloseOnOutside({
    isOpen: addDayDropdownOpen,
    onClose: () => setAddDayDropdownOpen(false),
    wrapRef: addDayWrapRef as any,
    enabled: true,
    gate: true
  });
  useCloseOnOutside({
    isOpen: addSelectMenuOpen,
    onClose: () => setAddSelectMenuOpen(false),
    wrapRef: addSelectMenuWrapRef as any,
    enabled: true,
    gate: true
  });

  React.useEffect(() => {
    if (!addSportIds || addSportIds.length === 0) return;
    const allowed = new Set((sportOptions || []).map(s => String(s.id)));
    const filtered = addSportIds.filter(id => allowed.has(String(id)));
    if (filtered.length !== addSportIds.length) setAddSportIds(filtered);
  }, [sportOptions, addSportIds.join('|')]);

  React.useEffect(() => {
    setAddAgeSearch('');
    setAddAgeDropdownOpen(false);
  }, [naming]);

  React.useEffect(() => {
    const clubIds = (addItems || [])
      .map(getClubId)
      .map(x => String(x || ''))
      .filter(Boolean);

    const toLoad = clubIds.filter(
      id =>
        !addCountsLoadingByClubId[id] &&
        (typeof addCountsByClubId[id]?.eligible !== 'number' || typeof addCountsByClubId[id]?.allow !== 'number')
    );

    if (!toLoad.length) return;

    toLoad.forEach(clubId => {
      setAddCountsLoadingByClubId(prev => ({ ...prev, [clubId]: true }));

      getClubStudentsCount(user, clubId)
        .then((eligibleRes: any) => {
          const eligible = Number(eligibleRes?.count || 0);
          setAddCountsByClubId(prev => ({ ...prev, [clubId]: { eligible, allow: 0 } }));
        })
        .catch(() => {
          setAddCountsByClubId(prev => ({ ...prev, [clubId]: { eligible: 0, allow: 0 } }));
        })
        .then(
          () => setAddCountsLoadingByClubId(prev => ({ ...prev, [clubId]: false })),
          () => setAddCountsLoadingByClubId(prev => ({ ...prev, [clubId]: false }))
        );
    });
  }, [user, addItems, addCountsByClubId, addCountsLoadingByClubId]);

  React.useEffect(() => {
    return () => {
      if (refreshProgrammeTimerRef.current) {
        window.clearTimeout(refreshProgrammeTimerRef.current);
      }
    };
  }, []);

  const buildAddWhereMemo = React.useCallback(() => {
    return buildAddWhere({
      addMode,
      addSportIds,
      addDays,
      addGenders,
      addAgeValues,
      addStartFrom,
      addStartTo,
      addSearch: appliedAddSearch,
      excludeClubIds
    });
  }, [
    addMode,
    addSportIds.join('|'),
    addDays.join('|'),
    addGenders.join('|'),
    addAgeValues.join('|'),
    addStartFrom,
    addStartTo,
    appliedAddSearch,
    excludeClubIds.join('|')
  ]);

  const loadAddPage = React.useCallback(
    (page: number) => {
      const where = buildAddWhereMemo();
      const requestId = ++addLoadRequestRef.current;

      setAddErr('');
      setIsAddWorking(true);

      setAddItems([]);
      setAddTotal(0);

      getClubsCount(user, where)
        .then((cnt: any) => {
          if (requestId !== addLoadRequestRef.current) return;

          const total = Number((cnt && (cnt as any).count) || 0);
          setAddTotal(total);

          const safePage = page < 1 ? 1 : page;
          const maxPages = Math.max(1, Math.ceil(total / LIMIT));
          const finalPage = safePage > maxPages ? maxPages : safePage;
          const skip = (finalPage - 1) * LIMIT;

          return getClubs(user, {
            limit: LIMIT,
            skip,
            where,
            sort: 'schedule.startDate ASC'
          } as any).then((items: any[]) => {
            if (requestId !== addLoadRequestRef.current) return;

            setAddItems(Array.isArray(items) ? items : []);
            setAddPage(finalPage);
            setIsAddWorking(false);
          });
        })
        .catch(e => {
          if (requestId !== addLoadRequestRef.current) return;

          setAddErr(getErrMsg(e, 'Failed to load clubs'));
          setAddItems([]);
          setAddTotal(0);
          setIsAddWorking(false);
        });
    },
    [user, buildAddWhereMemo]
  );

  React.useEffect(() => {
    addClearSelection();
    loadAddPage(1);
  }, [
    addMode,
    appliedAddSearch,
    addSportIds.join('|'),
    addDays.join('|'),
    addStartFrom,
    addStartTo,
    addGenders.join('|'),
    addAgeValues.join('|'),
    addClearSelection,
    loadAddPage
  ]);

  React.useEffect(() => {
    refreshSportsList();
  }, [addMode, refreshSportsList]);

  React.useEffect(() => {
    refreshFormsList();
  }, [refreshFormsList]);

  React.useEffect(() => {
    loadAddPage(addPage);
  }, [excludeClubIds.join('|')]);

  const addToggleSelect = (clubId: string) => {
    const id = String(clubId || '');
    if (!id) return;

    if (addAllSelected) {
      setAddDeselectedIds(prev => {
        const next = { ...prev };
        next[id] = !next[id];
        if (next[id] === false) delete next[id];
        return next;
      });
      return;
    }

    setAddSelectedIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const addSelectThisPage = (list: any[]) => {
    const ids = (list || []).map(getClubId).filter(Boolean);

    if (addAllSelected) {
      setAddDeselectedIds(prev => {
        const next = { ...prev };
        ids.forEach(id => {
          if (next[id]) delete next[id];
        });
        return next;
      });
      return;
    }

    setAddSelectedIds(prev => {
      const next = { ...prev };
      ids.forEach(id => (next[id] = true));
      return next;
    });
  };

  const addSelectAllMatching = () => {
    const where = buildAddWhereMemo();

    setIsAddWorking(true);
    setAddErr('');

    getClubs(user, {
      where,
      limit: 1000,
      skip: 0,
      sort: 'schedule.startDate ASC'
    } as any)
      .then((items: any[]) => {
        const ids = (Array.isArray(items) ? items : [])
          .map(getClubId)
          .map((x: any) => String(x || ''))
          .filter(Boolean);

        const nextSelected: { [id: string]: boolean } = {};
        ids.forEach(id => {
          nextSelected[id] = true;
        });

        setAddAllSelected(false);
        setAddAllSelectedWhere(null);
        setAddDeselectedIds({});
        setAddSelectedIds(nextSelected);
        setIsAddWorking(false);
      })
      .catch((e: any) => {
        setAddErr(getErrMsg(e, 'Failed to select matching clubs'));
        setIsAddWorking(false);
      });
  };

  const addGetBatchSelectionPayload = () => {
    if (addAllSelected) {
      const whereRaw = addAllSelectedWhere || addNormalizeWhereForSelectAll(buildAddWhereMemo());
      const exclude = addGetDeselectedExplicitIds();

      const out: any = {
        selectAll: true,
        filter: { where: whereRaw }
      };
      if (exclude.length) out.excludeClubIds = exclude;
      return out;
    }

    return {
      selectAll: false,
      clubIds: addGetSelectedExplicitIds()
    };
  };

  const onAddSelected = () => {
    setAddErr('');

    const selection = addGetBatchSelectionPayload();

    if (!selection.selectAll && (!selection.clubIds || selection.clubIds.length === 0)) {
      setAddErr('No clubs selected to add.');
      return;
    }

    setIsAddWorking(true);

    const payload = {
      ...selection,
      isEnabled: true,
      allowPastStartDate: addMode === 'SHOW_ALL'
    };

    addProgrammeClubsBatch(user, programmeId, payload)
      .then((updated: any) => {
        const updatedProgrammeId = String((updated as any)?.id || (updated as any)?._id || '');

        if (!updatedProgrammeId) {
          setAddErr(getErrMsg(updated, (updated && (updated as any).error) || 'Failed to add selected clubs'));
          setIsAddWorking(false);
          return;
        }

        onProgrammeUpdated(updated);

        const newIds = ((updated as any).clubs || []).map((pc: any) => String(pc && pc.clubId)).filter(Boolean);
        setExcludeClubIds(newIds);
        refreshProgrammeClubDetails();

        setIsAddWorking(false);
        addClearSelection();
      })
      .catch((e: any) => {
        setAddErr(getErrMsg(e, 'Failed to add selected clubs'));
        setIsAddWorking(false);
      });
  };

  const addFrom = addTotal === 0 ? 0 : (addPage - 1) * LIMIT + 1;
  const addTo = Math.min(addTotal, addPage * LIMIT);
  const addPages = Math.max(1, Math.ceil((addTotal || 0) / LIMIT));

  const addExcludedCount = Object.keys(addDeselectedIds).filter(k => addDeselectedIds[k]).length;
  const addIsPartialAll = addAllSelected && addExcludedCount > 0;

  if (!programme) return <Loader />;

  const renderModalRow = (club: any) => {
    const id = getClubId(club);
    const checked = addIsSelected(id);
    const day = getProgrammeClubDayShortLabel(getClubDay(club));
    const startDate = fmtDMY(getClubStartIso(club));
    const sportName = getClubSportName(club);
    const ages = getClubAgesLabel(club);
    const gender = getProgrammeClubGenderLabel(club);

    return (
      <label key={id} className={checked ? 'pcAddClubRow pcAddClubRow--selected' : 'pcAddClubRow'}>
        <input
          type="checkbox"
          checked={checked}
          disabled={!canEdit || isAddWorking}
          onChange={() => addToggleSelect(id)}
        />
        <span className="pcAddClubIcon" aria-hidden="true"><AddModalIcon icon="whistle" size={16} /></span>
        <span className="pcAddClubText">
          <span className="pcAddClubName">{String((club as any).name || '')}</span>
          <span className="pcAddClubMeta">{sportName || '-'} · {day || '-'} {startDate || '-'} · {gender || '-'} · {ages || '-'}</span>
        </span>
      </label>
    );
  };

  if (variant === 'modal') {
    return (
      <div className="pcAddModalPanel" onClick={event => event.stopPropagation()}>
        <div className="pcAddModalHead">
          <div className="pcAddModalTitleRow">
            <div className="pcAddModalTitle">Add clubs to programme</div>
            <button type="button" className="pcAddModalClose" aria-label="Close add clubs" onClick={onClose}>×</button>
          </div>
          <label className="pcAddModalSearch">
            <span className="pcAddModalSearchIcon" aria-hidden="true"><AddModalIcon icon="search" size={15} /></span>
            <input
              value={addSearch}
              placeholder="Search eligible clubs…"
              onChange={event => setAddSearch(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  setAppliedAddSearch(addSearch);
                }
              }}
            />
          </label>
        </div>

        <div className="pc-scroll pcAddModalList">
          {addErr ? <div className="pcAddModalError">{addErr}</div> : null}
          {isAddWorking && addItems.length === 0 ? (
            <div className="pcAddModalEmpty"><Loader /></div>
          ) : addItems.length ? (
            addItems.map(renderModalRow)
          ) : (
            <div className="pcAddModalEmpty">No eligible clubs to add.</div>
          )}
        </div>

        <div className="pcAddModalFoot">
          <div className="pcAddModalSelected">{addSelectedCount} selected</div>
          <div className="pcAddModalActions">
            <button type="button" className="pcButton pcButton--ghost" disabled={isAddWorking} onClick={onClose}>Cancel</button>
            <button type="button" className="pcButton pcButton--primary" disabled={!canEdit || isAddWorking || addSelectedCount === 0} onClick={onAddSelected}>
              {isAddWorking ? 'Adding...' : `Add ${addSelectedCount || ''}`.trim()}
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      {!canEdit ? (
        <div className="cpReadOnlyNote mb-3">
          This programme is not in <b>DRAFT</b> status, so clubs cannot be changed.
        </div>
      ) : null}

      <div className="cpModalShell">
        <ClubProgrammeAddClubsFilters
          isAddFiltersOpen={isAddFiltersOpen}
          setIsAddFiltersOpen={setIsAddFiltersOpen}
          programmeClubsLength={programmeClubs.length}
          isProgrammeClubsLoading={isProgrammeClubsLoading}
          isAddWorking={isAddWorking}
          isSportsLoading={isSportsLoading}
          addSearch={addSearch}
          setAddSearch={setAddSearch}
          onApplySearch={() => setAppliedAddSearch(addSearch)}
          addMode={addMode}
          setAddMode={setAddMode}
          addSportIds={addSportIds}
          setAddSportIds={setAddSportIds}
          addSportDropdownOpen={addSportDropdownOpen}
          setAddSportDropdownOpen={setAddSportDropdownOpen}
          addSportSearch={addSportSearch}
          setAddSportSearch={setAddSportSearch}
          addActivitiesWrapRef={addActivitiesWrapRef}
          sportOptions={sportOptions}
          addDays={addDays}
          setAddDays={setAddDays}
          addDayDropdownOpen={addDayDropdownOpen}
          setAddDayDropdownOpen={setAddDayDropdownOpen}
          addDayWrapRef={addDayWrapRef}
          addDaysLabel={addDaysLabel}
          addGenders={addGenders}
          setAddGenders={setAddGenders}
          addGenderDropdownOpen={addGenderDropdownOpen}
          setAddGenderDropdownOpen={setAddGenderDropdownOpen}
          addGenderWrapRef={addGenderWrapRef}
          addGenderOptions={addGenderOptions}
          addAgeValues={addAgeValues}
          setAddAgeValues={setAddAgeValues}
          addAgeDropdownOpen={addAgeDropdownOpen}
          setAddAgeDropdownOpen={setAddAgeDropdownOpen}
          addAgeSearch={addAgeSearch}
          setAddAgeSearch={setAddAgeSearch}
          addAgeWrapRef={addAgeWrapRef}
          addAgesLabel={addAgesLabel}
          ageOptions={ageOptions}
          naming={naming}
          addStartFrom={addStartFrom}
          setAddStartFrom={setAddStartFrom}
          addStartTo={addStartTo}
          setAddStartTo={setAddStartTo}
          columns={ADD_CLUBS_COLUMNS}
          hiddenColumns={addHiddenColumns}
          onToggleColumn={addToggleColumn}
          onResetColumns={() => setAddHiddenColumns([])}
          onResetFilters={() => {
            setAddSearch('');
            setAppliedAddSearch('');
            setAddMode('ELIGIBLE_ONLY');
            setAddSportIds([]);
            setAddDays([]);
            setAddStartFrom('');
            setAddStartTo('');
            setAddGenders(getInitialAddGenderFiltersFromProgramme());
            setAddAgeValues(getInitialAddAgeValuesFromProgramme());
            setAddSportSearch('');
            setAddAgeSearch('');
            setAddSportDropdownOpen(false);
            setAddGenderDropdownOpen(false);
            setAddAgeDropdownOpen(false);
            setAddDayDropdownOpen(false);
            addClearSelection();
          }}
        />
        {addErr ? <div className="text-danger mt-2">{addErr}</div> : null}

        <ClubProgrammeAddClubsActionBar
          canEdit={canEdit}
          isAddWorking={isAddWorking}
          addAllSelected={addAllSelected}
          addSelectedCount={addSelectedCount}
          addHasAnySelection={addHasAnySelection}
          addIsPartialAll={addIsPartialAll}
          addItems={addItems}
          addTotal={addTotal}
          addPage={addPage}
          addPages={addPages}
          addFrom={addFrom}
          addTo={addTo}
          loadAddPage={loadAddPage}
          addSelectMenuOpen={addSelectMenuOpen}
          setAddSelectMenuOpen={setAddSelectMenuOpen}
          addSelectMenuWrapRef={addSelectMenuWrapRef}
          addSelectThisPage={addSelectThisPage}
          addSelectAllMatching={addSelectAllMatching}
          addClearSelection={addClearSelection}
          onAddSelected={onAddSelected}
        />

        <ClubProgrammeAddClubsTable
          addItems={addItems}
          addMode={addMode}
          canEdit={canEdit}
          isAddWorking={isAddWorking}
          addCountsByClubId={addCountsByClubId}
          addCountsLoadingByClubId={addCountsLoadingByClubId}
          addIsSelected={addIsSelected}
          addToggleSelect={addToggleSelect}
          getClubAgesLabel={getClubAgesLabel}
          getClubBadges={getClubBadges}
          hiddenColumns={addHiddenColumns}
        />
      </div>
    </>
  );
};
